import { inputFileBase64toApiBase64 } from 'affiny-core/helpers/picture/picture.helper';
import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { trackEventSaga } from 'affiny-core/services/tracking/saga';
import { TrackerEvents } from 'affiny-core/services/tracking/tracker';
import { all, call, cps, put, spawn, takeLatest } from 'redux-saga/effects';
import { toDataUrl } from '../../services/utils';
import { AccountActions } from '../account/actions';
import { getAccountPicturesSaga } from '../account/saga';
import { postPicture } from '../api';
import {
  deletePicture,
  getPictures,
  setMainPicture,
} from '../api/repositories/picture/picture.sagas';
import { ActionsOfType } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import {
  deletePictureSuccessAction,
  EPicturesActionType,
  getPicturesSuccessAction,
  IDeletePictureAction,
  IGetPicturesAction,
  IPicturesActionCreator,
  IPostPictureByUriAction,
  IPostPicturesAction,
  postPicturesAction,
  postPicturesSuccessAction,
} from './actions';
import { IPictureEntries } from './types';

export function* postPictureSaga(action: IPostPicturesAction) {
  const response: IPictureEntries = yield call(postPicture, action.payload);

  yield put(postPicturesSuccessAction(response));
  yield spawn(trackEventSaga, TrackerEvents.UPLOAD_PHOTO_COMPLETE);

  if (action.payload.refreshPictures) {
    yield put(AccountActions.getPicturesAccount());
  }
}

export function* postPictureByUriSaga(action: IPostPictureByUriAction) {
  const res = yield cps(toDataUrl, action.payload.uri);

  if (res) {
    yield call(
      postPictureSaga,
      postPicturesAction({
        imageBase64: inputFileBase64toApiBase64(res),
        type: 'portrait',
        refreshPictures: true,
      })
    );
  } else {
    throw Error('error while posting picture by uri');
  }
}

export function* setMainPictureSaga(
  action: ActionsOfType<IPicturesActionCreator, EPicturesActionType.SET_MAIN>
) {
  yield call(setMainPicture, action.payload);
  yield call(getAccountPicturesSaga);
}

export function* getPicturesSaga(action: IGetPicturesAction) {
  if (!(yield* checkHasCompletedProfile())) return;

  const response = yield call(getPictures, action.payload);

  yield put(getPicturesSuccessAction(response));
}

export function* deletePicturesSaga(action: IDeletePictureAction) {
  yield call(deletePicture, action.payload);
  yield put(deletePictureSuccessAction(action.payload.id));
  yield put(AccountActions.getPicturesAccount());
}

export function* getPicturesCallSaga(action: IGetPicturesAction) {
  const response = yield call(getPictures, action.payload);

  yield put(getPicturesSuccessAction(response));
  yield put(AccountActions.getPicturesAccountSuccess(response));
}

// Saga to get the user picture during PC and check
// if the user did cross sell and has a picture set
// on the Meetic website
export function* getPicturesAsyncSaga(action: IGetPicturesAction) {
  yield spawn(getPicturesCallSaga, action);
}

export function* picturesRootSaga() {
  yield all([
    takeLatest(
      EPicturesActionType.GET,
      handleAsyncStatus(getPicturesSaga, EAsyncEventTypes.GET_PICTURES)
    ),
    takeLatest(EPicturesActionType.GET_ASYNC, getPicturesAsyncSaga),
    takeLatest(
      EPicturesActionType.DELETE,
      handleAsyncStatus(deletePicturesSaga, EAsyncEventTypes.DELETE_PICTURES)
    ),
    takeLatest(
      EPicturesActionType.POST,
      handleAsyncStatus(postPictureSaga, EAsyncEventTypes.POST_PICTURE)
    ),
    takeLatest(
      EPicturesActionType.SET_MAIN,
      handleAsyncStatus(
        setMainPictureSaga,
        EAsyncEventTypes.SET_MAIN_PICTURE,
        true
      )
    ),
    takeLatest(
      EPicturesActionType.POST_BY_URI,
      handleAsyncStatus(postPictureByUriSaga, EAsyncEventTypes.POST_PICTURE)
    ),
  ]);
}
